import React from 'react';
import type { AttributeScope } from '../../../Campaigns/guided/config/attributes';

/**
 * Ambient authoring context for the `{{`-token drop-in fields ({@link TokenAwareTextField}).
 *
 * The drop-in replaces MUI `TextField` across the campaign admin with no per-call-site prop threading —
 * it reads the campaign it's authoring, how far into the step order it is, and its authoring scope from
 * this context instead. A campaign builder wraps its editor tree in {@link TokenFieldProvider
 * campaignId={campaign.id}} once and every enhanced field below it offers that campaign's variables.
 * Outside any provider the value is empty (campaignId `undefined`), so a stray field still works — it
 * simply offers only the universal/static tokens the catalog defines without a campaign.
 *
 * `scope` gates which attribute GROUPS the picker offers: `campaign` (the default) hides delivery-scoped
 * groups (customer / Salesforce / captured answers / gaps / deep-dive) that can never resolve in an
 * enablement field; a per-delivery surface nests a `scope="delivery"` provider to unlock them. A nested
 * provider merges with its parent, so it can override scope alone without re-supplying `campaignId`.
 */
export interface TokenFieldContextValue {
  campaignId: number | undefined;
  /** Step order to gate not-yet-available dynamic options by; omitted = unrestricted. */
  uptoStepOrder?: number;
  /** Authoring scope for the picker's group filter. Defaults to `campaign` (enablement-safe). */
  scope: AttributeScope;
}

const TokenFieldContext = React.createContext<TokenFieldContextValue>({
  campaignId: undefined,
  scope: 'campaign',
});

export function TokenFieldProvider(props: {
  campaignId?: number | undefined;
  uptoStepOrder?: number;
  scope?: AttributeScope;
  children: React.ReactNode;
}): JSX.Element {
  const { campaignId, uptoStepOrder, scope, children } = props;
  const parent = React.useContext(TokenFieldContext);
  // Merge with the parent so a nested provider can override one field (e.g. `scope="delivery"` on a
  // per-delivery surface) without re-declaring the campaign it inherited.
  const nextCampaignId = campaignId !== undefined ? campaignId : parent.campaignId;
  const nextUptoStepOrder = uptoStepOrder !== undefined ? uptoStepOrder : parent.uptoStepOrder;
  const nextScope = scope ?? parent.scope;
  const value = React.useMemo<TokenFieldContextValue>(
    () => ({ campaignId: nextCampaignId, uptoStepOrder: nextUptoStepOrder, scope: nextScope }),
    [nextCampaignId, nextUptoStepOrder, nextScope],
  );
  return <TokenFieldContext.Provider value={value}>{children}</TokenFieldContext.Provider>;
}

export function useTokenFieldContext(): TokenFieldContextValue {
  return React.useContext(TokenFieldContext);
}

export default TokenFieldContext;
